// EMBED-RERALM-SOURCE-TEXT-GATE-1 — the STRUCTURAL gate for `rerealmFunctions`.
//
// WHAT THIS REPLACES. The gate used to be a substring test:
//
//     const GLOBAL_REF_RE = /\b(window|document)\s*\./
//     if (NATIVE_RE.test(src) || !GLOBAL_REF_RE.test(src)) continue
//
// A function was re-realmed into the iframe realm ONLY if its own source text
// literally contained `window.` or `document.`. Everything that reaches a
// realm-bound global another way was silently skipped: `globalThis.`, `self.`,
// `top.`/`parent.`, an alias declared inside the function (`const w = window`),
// a bare call (`requestAnimationFrame(…)`, `new Image()`), or a bare read
// (`location`, `navigator`, `localStorage`).
//
// WIDENING THE REGEX IS THE TRAP. `window|document|globalThis|self|top` makes
// the next four shapes pass and the fifth fail, one level along. The gate has
// to be derived from the function's STRUCTURE, not from a substring of it.
//
// WHAT THIS IS. A dependency-free scanner that computes the function's FREE
// IDENTIFIERS — identifiers it reads that it does not itself bind (not params,
// not `var`/`let`/`const`/`function`/`class`/`catch` bindings, not member
// property names after `.`/`?.`, not object-literal keys, not anything inside a
// string, template chunk, regex literal or comment). The gate is then exact:
//
//     re-realm  ⇔  the function has a free identifier that is realm-bound
//
// WHY NOT `acorn`. acorn is present in the tree only as a hoisted transitive
// dep, and this file is on the embed BOOT path — `createIframeApp.js` already
// refuses the `smbls` barrel and dynamic-imports `create` for exactly that
// budget reason (see its header). A ~120 KB parser to answer one boolean would
// undo that. This scanner is ~5 KB and answers the same boolean.
//
// WHY NOT "re-realm everything". Measured, and it is wrong on BOTH axes —
// see the numbers and the double-side-effect proof in `rerealmGate.test.js`.
// `frameWindow.eval('(' + src + ')')` recompiles the function in the FRAME's
// GLOBAL scope, so it DROPS THE CLOSURE. A function that closes over anything
// then throws `ReferenceError` on call and is re-run in the parent realm by the
// fallback wrapper — after any side effect it already performed. Re-realming
// unconditionally turns every closure-using function into a
// may-run-its-side-effects-twice function. The free-identifier analysis is what
// tells the two apart, so it buys correctness, not only speed.

// MEASURED, live, on the bellforge canvas board (23 embed tiles, Chrome,
// 2026-08-21) over 5550 real function sources reachable from the running
// shell context:
//   old text gate selected  252
//   this gate selected      420   (+168 net)
//   newly caught            193 — globalThis 57, setTimeout 49, window 37
//                           (free but never as `window.`), clearTimeout 33,
//                           localStorage 15, document 12, clearInterval 7,
//                           fetch 5, HTMLElement 5, navigator 5,
//                           requestAnimationFrame 4, self 4
//   no longer caught         25 — the old gate's FALSE positives: `window.` /
//                           `document.` occurring only inside a string, a
//                           comment, a regex literal, a member expression or
//                           an object key. Each of those cost a dropped
//                           closure for nothing.
// Cost on the same population, real tile `contentWindow`: scan 12.6 µs/fn
// (53.1 ms) vs unconditional frame-eval 31.5 µs/fn (132.9 ms) — the gate is
// ~2.5x cheaper per boot AND keeps the closures.

// The set is realm-BOUND, not "every global". A name belongs here only when
// resolving it in the PARENT realm from inside a frame is a bug: the parent's
// object is a different object (`document`, `location`, `localStorage`), or the
// work it schedules/creates is owned by the wrong document (timers, observers,
// `Image`, DOM constructors used with `instanceof`). Pure, realm-agnostic
// globals (`JSON`, `Math`, `Object`, `Promise`, `Date`, `Symbol`, …) are
// deliberately ABSENT — re-realming for them would cost a closure for nothing.
//
// This list is the SINGLE source of truth. `rerealmFunctions` names none of
// these; the test enumerates its cases FROM this set, so a name added here is
// covered without touching either file.
export const REALM_BOUND_GLOBALS = new Set([
  // the realm handles themselves
  'window', 'document', 'globalThis', 'self', 'top', 'parent', 'frames',
  'frameElement',
  // per-document singletons
  'location', 'navigator', 'history', 'screen', 'visualViewport',
  'localStorage', 'sessionStorage', 'indexedDB', 'caches', 'customElements',
  'performance', 'crypto', 'speechSynthesis',
  // per-document geometry / view
  'innerWidth', 'innerHeight', 'outerWidth', 'outerHeight', 'devicePixelRatio',
  'scrollX', 'scrollY', 'pageXOffset', 'pageYOffset', 'screenX', 'screenY',
  'matchMedia', 'getComputedStyle', 'getSelection', 'scrollTo', 'scrollBy',
  // scheduling — a parent-realm timer outlives the frame it was meant for
  'setTimeout', 'setInterval', 'clearTimeout', 'clearInterval',
  'requestAnimationFrame', 'cancelAnimationFrame',
  'requestIdleCallback', 'cancelIdleCallback', 'queueMicrotask',
  // network / transport bound to the document's origin + lifetime
  'fetch', 'XMLHttpRequest', 'WebSocket', 'EventSource', 'Worker',
  'SharedWorker', 'BroadcastChannel', 'navigatorCredentials',
  // constructors whose instances belong to a document
  'Image', 'Audio', 'Option', 'FileReader', 'DOMParser', 'XSLTProcessor',
  'MutationObserver', 'ResizeObserver', 'IntersectionObserver',
  'PerformanceObserver',
  // cross-realm `instanceof` — the classic silent false
  'Node', 'Element', 'HTMLElement', 'SVGElement', 'DocumentFragment',
  'ShadowRoot', 'Event', 'CustomEvent', 'MouseEvent', 'KeyboardEvent',
  'PointerEvent', 'Range', 'Selection', 'CSSStyleSheet', 'FormData',
  // window-level verbs
  'alert', 'confirm', 'prompt', 'open', 'close', 'print', 'focus', 'blur',
  'postMessage', 'addEventListener', 'removeEventListener', 'dispatchEvent'
])

// Built FROM the set above, never hand-typed — a name added there is in the
// prefilter automatically. Word-boundary only; the real decision is the scan.
const CHEAP_RE = new RegExp(
  '\\b(' + [...REALM_BOUND_GLOBALS].join('|') + ')\\b'
)

// Reserved words never count as identifier references.
const KEYWORDS = new Set([
  'await', 'break', 'case', 'catch', 'class', 'const', 'continue', 'debugger',
  'default', 'delete', 'do', 'else', 'enum', 'export', 'extends', 'false',
  'finally', 'for', 'function', 'if', 'import', 'in', 'instanceof', 'let',
  'new', 'null', 'of', 'return', 'super', 'switch', 'this', 'throw', 'true',
  'try', 'typeof', 'var', 'void', 'while', 'with', 'yield', 'async', 'static',
  'get', 'set'
])

// `(` groups that follow one of these are control-flow heads, never parameters.
const CONTROL_HEADS = new Set([
  'if', 'for', 'while', 'switch', 'return', 'typeof', 'void', 'delete', 'in',
  'of', 'new', 'do', 'else', 'case', 'yield', 'await', 'throw'
])

const isIdentStart = (c) => (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') ||
  c === '_' || c === '$'
const isIdentPart = (c) => isIdentStart(c) || (c >= '0' && c <= '9')
const isDigit = (c) => c >= '0' && c <= '9'

// Token shape: { t: 'ident' | 'punc' | 'num', v: string }
// Strings, template chunks, regex literals and comments produce NO token —
// their contents can never be an identifier reference. `${…}` interpolations
// DO produce tokens: `` `${document.title}` `` really does read `document`.
function tokenize (src) {
  const out = []
  const tmplStack = []  // brace depth at each open `${`
  let braceDepth = 0
  let i = 0
  const n = src.length
  const prev = () => out.length ? out[out.length - 1] : null

  const readTemplate = () => {
    // positioned just after the opening backtick
    while (i < n) {
      const c = src[i]
      if (c === '\\') { i += 2; continue }
      if (c === '`') { i++; return }
      if (c === '$' && src[i + 1] === '{') {
        i += 2
        tmplStack.push(braceDepth)
        braceDepth++
        out.push({ t: 'punc', v: '{' })
        return
      }
      i++
    }
  }

  while (i < n) {
    const c = src[i]
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r' || c === '\f' ||
      c === '\v' || c === ' ') { i++; continue }
    if (c === '/' && src[i + 1] === '/') {
      while (i < n && src[i] !== '\n') i++
      continue
    }
    if (c === '/' && src[i + 1] === '*') {
      i += 2
      while (i < n && !(src[i] === '*' && src[i + 1] === '/')) i++
      i += 2
      continue
    }
    if (c === '"' || c === "'") {
      i++
      while (i < n && src[i] !== c) { i += src[i] === '\\' ? 2 : 1 }
      i++
      out.push({ t: 'str', v: '' })
      continue
    }
    if (c === '`') { i++; readTemplate(); continue }
    if (c === '/') {
      // regex vs division: a regex can only start where a VALUE can start.
      const p = prev()
      const divides = p && (
        p.t === 'num' || p.t === 'str' ||
        (p.t === 'ident' && !KEYWORDS.has(p.v)) ||
        (p.t === 'punc' && (p.v === ')' || p.v === ']' || p.v === '}' ||
          p.v === '++' || p.v === '--'))
      )
      if (!divides) {
        i++
        let inClass = false
        while (i < n) {
          const d = src[i]
          if (d === '\\') { i += 2; continue }
          if (d === '[') inClass = true
          else if (d === ']') inClass = false
          else if (d === '/' && !inClass) { i++; break }
          else if (d === '\n') break
          i++
        }
        while (i < n && isIdentPart(src[i])) i++  // flags
        out.push({ t: 'str', v: '' })
        continue
      }
      // division / `/=`
      i += src[i + 1] === '=' ? 2 : 1
      out.push({ t: 'punc', v: '/' })
      continue
    }
    if (isDigit(c) || (c === '.' && isDigit(src[i + 1]))) {
      i++
      while (i < n && (isIdentPart(src[i]) || src[i] === '.')) i++
      out.push({ t: 'num', v: '' })
      continue
    }
    if (isIdentStart(c)) {
      const s = i
      while (i < n && isIdentPart(src[i])) i++
      out.push({ t: 'ident', v: src.slice(s, i) })
      continue
    }
    // punctuation
    if (c === '{') { braceDepth++; i++; out.push({ t: 'punc', v: '{' }); continue }
    if (c === '}') {
      braceDepth--
      i++
      out.push({ t: 'punc', v: '}' })
      if (tmplStack.length && tmplStack[tmplStack.length - 1] === braceDepth) {
        tmplStack.pop()
        readTemplate()
      }
      continue
    }
    const three = src.slice(i, i + 3)
    const two = src.slice(i, i + 2)
    if (three === '...' || three === '**=' || three === '===' || three === '!==') {
      i += 3
      out.push({ t: 'punc', v: three })
      continue
    }
    if (two === '=>' || two === '?.' || two === '++' || two === '--' ||
      two === '==' || two === '!=' || two === '&&' || two === '||' ||
      two === '??' || two === '+=' || two === '-=' || two === '*=' ||
      two === '<=' || two === '>=' || two === '**') {
      i += 2
      out.push({ t: 'punc', v: two })
      continue
    }
    i++
    out.push({ t: 'punc', v: c })
  }
  return out
}

// Mark every identifier the function BINDS itself. Anything left is free.
function markBindings (tk, bound) {
  const n = tk.length
  // 1. parameter lists: `(…)` followed by `=>`, or `(…)` that heads a function
  //    body — `function f (a) {`, `foo (a) {` (method shorthand), `catch (e) {`.
  //    Also the single-identifier arrow head `x => …`.
  const openOf = new Array(n).fill(-1)
  const stack = []
  for (let i = 0; i < n; i++) {
    if (tk[i].t !== 'punc') continue
    if (tk[i].v === '(') stack.push(i)
    else if (tk[i].v === ')') {
      const o = stack.pop()
      if (o !== undefined) openOf[i] = o
    }
  }
  for (let i = 0; i < n; i++) {
    const t = tk[i]
    if (t.t === 'ident' && !KEYWORDS.has(t.v) && tk[i + 1] &&
      tk[i + 1].t === 'punc' && tk[i + 1].v === '=>') {
      bound.add(t.v)
      continue
    }
    if (t.t !== 'punc' || t.v !== ')' || openOf[i] < 0) continue
    const nx = tk[i + 1]
    const before = tk[openOf[i] - 1]
    const isArrowHead = nx && nx.t === 'punc' && nx.v === '=>'
    const isFnHead = nx && nx.t === 'punc' && nx.v === '{' &&
      (!before ||
        (before.t === 'ident' && !CONTROL_HEADS.has(before.v)) ||
        (before.t === 'punc' && (before.v === '*' || before.v === '(')))
    if (!isArrowHead && !isFnHead) continue
    // Bind the names, skipping default-value expressions (`a = window.x`).
    let depth = 0
    let inDefault = false
    for (let j = openOf[i] + 1; j < i; j++) {
      const p = tk[j]
      if (p.t === 'punc') {
        if (p.v === '(' || p.v === '[' || p.v === '{') depth++
        else if (p.v === ')' || p.v === ']' || p.v === '}') depth--
        else if (p.v === ',' && depth === 0) inDefault = false
        else if (p.v === '=' && !inDefault) inDefault = true
        continue
      }
      if (p.t === 'ident' && !inDefault && !KEYWORDS.has(p.v)) {
        const pv = tk[j - 1]
        if (pv && pv.t === 'punc' && (pv.v === '.' || pv.v === '?.')) continue
        // `{ a: renamed }` — only the RIGHT side is the binding
        if (tk[j + 1] && tk[j + 1].t === 'punc' && tk[j + 1].v === ':') continue
        bound.add(p.v)
      }
    }
  }
  // 2. declarations: `var`/`let`/`const` binding names, `function f`, `class C`.
  for (let i = 0; i < n; i++) {
    const t = tk[i]
    if (t.t !== 'ident') continue
    if (t.v === 'function' || t.v === 'class') {
      const nx = tk[i + 1]
      if (nx && nx.t === 'ident' && !KEYWORDS.has(nx.v)) bound.add(nx.v)
      continue
    }
    if (t.v !== 'var' && t.v !== 'let' && t.v !== 'const') continue
    let depth = 0
    let inValue = false
    for (let j = i + 1; j < n; j++) {
      const p = tk[j]
      if (p.t === 'punc') {
        if (p.v === '(' || p.v === '[' || p.v === '{') depth++
        else if (p.v === ')' || p.v === ']') depth--
        else if (p.v === '}') { depth--; if (depth < 0) break }
        else if (p.v === ',' && depth === 0) inValue = false
        else if (p.v === ',' && depth > 0 && !inValue) continue
        else if (p.v === ';') break
        else if (p.v === '=' && !inValue) inValue = true
        continue
      }
      if (p.t === 'ident') {
        if (KEYWORDS.has(p.v)) {
          // `for (const x of list)` / `in` — the declarator ends here
          if (p.v === 'of' || p.v === 'in') break
          continue
        }
        if (inValue) continue
        const pv = tk[j - 1]
        if (pv && pv.t === 'punc' && (pv.v === '.' || pv.v === '?.')) continue
        if (tk[j + 1] && tk[j + 1].t === 'punc' && tk[j + 1].v === ':') continue
        bound.add(p.v)
      } else if (p.t === 'num' || p.t === 'str') {
        if (!inValue && depth === 0) break
      }
    }
  }
}

/**
 * Free identifiers of a function source — every name it READS and does not
 * itself bind. Member property names (`x.window`), object-literal keys
 * (`{ document: 1 }`), string/template/regex/comment contents and reserved
 * words are excluded.
 * @param {string} src `Function.prototype.toString` output.
 * @returns {Set<string>}
 */
export function freeIdentifiers (src) {
  const tk = tokenize(src)
  const bound = new Set()
  markBindings(tk, bound)
  const free = new Set()
  for (let i = 0; i < tk.length; i++) {
    const t = tk[i]
    if (t.t !== 'ident' || KEYWORDS.has(t.v) || bound.has(t.v)) continue
    const pv = tk[i - 1]
    if (pv && pv.t === 'punc' && (pv.v === '.' || pv.v === '?.')) continue
    // object-literal / class member KEY, e.g. `{ document: … }`, `case x:`
    const nx = tk[i + 1]
    if (nx && nx.t === 'punc' && nx.v === ':' && pv && pv.t === 'punc' &&
      (pv.v === '{' || pv.v === ',')) continue
    free.add(t.v)
  }
  return free
}

/**
 * THE GATE. True when the function reads a realm-bound global as a free
 * identifier, so re-realming it changes which realm's object it gets.
 *
 * Deliberately FALSE for a function whose only route to a global is a closure
 * variable declared OUTSIDE it (`const w = window` in an enclosing scope, then
 * `w.foo`) or a helper it calls (`helper()` where `helper` touches the global).
 * That is not a miss — it is the correct answer. Re-realming drops the closure,
 * so `w`/`helper` would be undefined in the frame and the function would
 * `ReferenceError` on its first call. Those routes are fixed at the layer that
 * owns them: the helper is re-realmed on its own when it is reachable on the
 * walked tree (it gets the frame's globals, and the caller keeps working), and
 * a host-realm binding is folded in by `hostContext` (EMBED-REALM-FN-FOLD-1).
 *
 * @param {string} src `Function.prototype.toString` output.
 * @returns {boolean}
 */
export function usesRealmBoundGlobal (src) {
  if (typeof src !== 'string' || !src) return false
  // Cheap reject: not one realm-bound name appears anywhere in the text, so no
  // amount of scanning can find one free. Skips the tokenizer for the majority
  // of a published tree's functions (pure data/props lambdas).
  if (!CHEAP_RE.test(src)) return false
  for (const id of freeIdentifiers(src)) {
    if (REALM_BOUND_GLOBALS.has(id)) return true
  }
  return false
}

